import type {MouseEvent, ReactElement} from 'react';
import {useRef, useState} from 'react';
import type {GeneratedVoiceItemProps} from './generated-voice-item.types';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize} from '@Components/icon-v2/icon.types';
import {getRandomThumbnailImageForAudioItem} from '@Libraries/poster-audio-item.library';
import styles from './generated-voice-item.module.scss';
import {ClickableDiv} from '@Components/clickable-div';
import {copyToClipboard} from '@Utils/clipboard.util';
import Tooltip from 'react-bootstrap/esm/Tooltip';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import {useAudioPlayback} from '@Hooks/ai-text-to-speech/useAudioPlayback';
import {useAppSelector} from '@/hooks';
import {GENERATED_VOICE_GRID_ID} from '@Panels/ai-voice-panel/components/generated-voice-list/generated-voice-list.types';
import {PlayPauseButton} from '@Panels/ai-voice-panel/components/play-pause-button/play-pause-button';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';

const COPY_TOOLTIP_DURATION = 2000;

export function GeneratedVoiceItem(props: GeneratedVoiceItemProps): ReactElement<HTMLDivElement> {
  const {id, title, duration, dateLabel, source, avatarUrl, height} = props;
  const avatarSrc = useRef(avatarUrl ?? getRandomThumbnailImageForAudioItem());
  const [isHovering, setIsHovering] = useState(false);
  const [isTitleCopied, setIsTitleCopied] = useState<boolean | undefined>(undefined);
  const audioPlayback = useAudioPlayback();
  const copyTooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSelected = useAppSelector((state) => {
    return !!state.grids.gridSelectionGroupHashmap[GENERATED_VOICE_GRID_ID]?.selectedItemIdHashmap[id];
  });

  const formatDuration = (playing: boolean, currentSeek: number, totalDuration: number): string => {
    const format = (seconds: number): string => {
      return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
    };
    const total = format(totalDuration);
    if (!playing) {
      return total;
    }
    return `${format(currentSeek)} / ${total}`;
  };

  const handlePlayPauseClick = (e: MouseEvent): void => {
    e.stopPropagation();
    audioPlayback.togglePlayPause(source);
  };

  const onCopyPrompt = (e: MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    trackPosterBuilderGA4Events(GA4EventName.AI_VOICE_COPY_PROMPT);
    if (copyTooltipTimeoutRef.current) {
      clearTimeout(copyTooltipTimeoutRef.current);
    }
    void copyToClipboard(title);
    setIsTitleCopied(true);
    copyTooltipTimeoutRef.current = setTimeout(() => {
      setIsTitleCopied(undefined);
    }, COPY_TOOLTIP_DURATION);
  };

  const getFormattedDate = (timestamp: number): string => {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
    }).format(timestamp * 1000);
  };

  return (
    <ClickableDiv
      style={height === undefined ? undefined : {height}}
      className={`flexbox flex-align-items-center radius-12 gap-3 _full-width _cursor-pointer spacing-p-t-2 spacing-p-b-2 spacing-p-l-3 spacing-p-r-3 hover-transition-all ${styles.container} ${isSelected ? styles.containerSelected : ''} ${height === undefined ? '' : styles.containerTall}`}
      onClick={() => {
        props.onClick?.();
      }}
      onMouseEnter={() => {
        setIsHovering(true);
      }}
      onMouseLeave={() => {
        setIsTitleCopied(undefined);
        setIsHovering(false);
      }}
    >
      <div className={`flexbox _position-relative ${styles.avatarContainer}`}>
        <img className={styles.avatar} src={avatarSrc.current} alt={title} loading="lazy" />
      </div>
      <div className={`flex-column-align-start ${styles.infoContainer}`}>
        <div className={`flexbox flex-align-items-center flex-content-space-between _full-width ${styles.titleRow}`}>
          <Text className={'-truncated-text _full-width'} bold size={TextSize.XXSMALL} val={title} />
          {isHovering ? (
            <OverlayTrigger
              overlay={
                <Tooltip id={`copy-tooltip-${id}`} className={`radius-8 ${styles.tooltip}`}>
                  <div className={'flex-row-align-center flex-justify-between'}>
                    <Text val={window.i18next.t(isTitleCopied ? 'pmwjs_copied' : 'pmwjs_copy_prompt')} size={TextSize.XSMALL} />
                  </div>
                </Tooltip>
              }
              offset={[0, 4]}
              trigger={['hover', 'focus']}
              show={isTitleCopied}
            >
              <ClickableDiv className={`flex-center radius-4 spacing-m-l-1 ${styles.copyIcon}`} onClick={onCopyPrompt}>
                <Icon icon={'icon-copy'} size={IconSize.SIZE_ICON_16} shape={IconShape.NONE} />
              </ClickableDiv>
            </OverlayTrigger>
          ) : null}
        </div>
        <div className={'flexbox gap-1 flex-align-items-center'}>
          <Text
            size={TextSize.XXSMALL}
            className={audioPlayback.isPlaying ? 'content-primary' : ''}
            val={formatDuration(audioPlayback.isPlaying, audioPlayback.seekTime, duration)}
          />
          <span className={styles.separatingDot} aria-hidden={true}>
            ■
          </span>
          <Text size={TextSize.XXSMALL} className="ct-neutral-primary-disabled" val={getFormattedDate(dateLabel)} />
        </div>
      </div>
      <PlayPauseButton isPlaying={audioPlayback.isPlaying} playbackProgress={audioPlayback.playbackProgress} onClick={handlePlayPauseClick} className={styles.playPauseOverlay} />
    </ClickableDiv>
  );
}
